import {
  useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  ExSideDrawer,
  ButtonType,
  ButtonSize,
  ExButton,
  ExInput,
  ExLoader,
  LoaderSize,
  LoaderVariant,
  ButtonFlavor,
  SearchSize,
  SearchFlavor,
  ExIcon,
  IconVariant,
  ExTable,
  ExPagination,
  ExTooltip,
  TooltipPosition,
  TooltipAlignment,
} from '@boomi/exosphere';
import { createRoot } from 'react-dom/client';
import './ModelAgentsDrawer.scss';
import { providerLogo } from '@components/ModelsManagement';
import DisplayProfilePicture from '@components/AgentBuilderWizard/DisplayProfilePicture/DisplayProfilePicture';
import { getDeployments } from '@services/agent-foundation/agent-foundation-deployments-api-service';
import { handleError } from '@services/agent-foundation/agent-foundation-api-service';
import { formatDate } from '@components/ListViewUtils/ListViewUtils';

const PAGE_SIZE = 10;

interface ModelAgentsDrawerProps {
  open: boolean;
  modelId: string;
  modelConfigId: string;
  modelName: string;
  provider?: string;
  navigation?: boolean;
  fromConfigurationsDrawer?: boolean;
  onClose: () => void;
  onParentClose?: () => void;
}

function ModelAgentsDrawer({
  open,
  modelId,
  modelName,
  modelConfigId,
  provider = 'Boomi',
  navigation = false,
  fromConfigurationsDrawer = false,
  onClose,
  onParentClose,
}: ModelAgentsDrawerProps) {
  const handleClose = () => {
    onClose();
    onParentClose?.();
  };
  const [t] = useTranslation('common');
  const [isLoading, setIsLoading] = useState(false);
  const [agents, setAgents] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedPage, setSelectedPage] = useState(1);
  const pageSize = PAGE_SIZE;
  const [gridApi, setGridApi] = useState<any>(null);
  const totalRowsRef = useRef(0);

  useEffect(() => {
    if (!open) return;
    setSelectedPage(1);
    setSearchTerm('');
    setDebouncedSearchTerm('');
  }, [open, modelId, modelConfigId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!open || !modelId) return;

    setIsLoading(true);
    const queryParams: Record<string, any> = {
      page: selectedPage,
      page_size: pageSize,
      model_id: modelId,
      deployment_status: 'ACTIVE',
    };
    if (modelConfigId) queryParams.model_config_id = modelConfigId;
    if (debouncedSearchTerm) queryParams.search_term = debouncedSearchTerm;

    getDeployments(queryParams)
      .then((res: any) => {
        if (!res?.success) {
          handleError(res, 'Failed to load deployments');
          setAgents([]);
          totalRowsRef.current = 0;
          return;
        }

        setAgents(res.data?.items ?? []);
        totalRowsRef.current = res.data?.total_items ?? 0;
      })
      .catch((err) => {
        handleError(err, 'Failed to load deployments');
        setAgents([]);
        totalRowsRef.current = 0;
      })
      .finally(() => setIsLoading(false));
  }, [open, modelId, modelConfigId, selectedPage, pageSize, debouncedSearchTerm]);

  const agentNameCellRenderer = (params: any) => {
    const { profile_picture: profilePicture } = params.data;
    const container = document.createElement('div');
    container.className = 'model-agents-drawer__name-cell';
    const root = createRoot(container);

    const profilePictureStyle = `
      .display-icon-container {
        display: flex;
        width: 28px;
        height: 28px;
        border-radius: 4px;
        justify-content: center;
        align-items: center;
        color: var(--exo-color-font-inverse);
      }
      .icon-seabreeze {
        background: linear-gradient(45deg, #4B4FE2 0%, #0EC38B 100%);
      }
      .icon-sunset {
        background: linear-gradient(45deg, #A03291 0%, #FF7C66 100%);
      }
      .icon-lavender {
        background: linear-gradient(45deg, #A03291 0%, #4B4FE2 100%);
      }
      .icon-midnight {
        background: linear-gradient(45deg, #02293B 0%, #0085F0 100%);
      }
      .display-icon-medium {
        &::part(icon) {
          width: 20px;
          height: 20px;
        }
      }
    `;

    root.render(
      <div style={{
        display: 'flex',
        alignItems: 'center',
        maxWidth: '100%',
        paddingTop: '4px',
      }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: '2px',
        }}
        >
          <DisplayProfilePicture
            selectedAvatar={profilePicture?.role}
            selectedColor={profilePicture?.colour}
            iconSize="M"
            profilePictureStyle={profilePictureStyle}
            useInlineStyle
          />
        </div>
        <ExTooltip
          tooltipDelay={0}
          position={TooltipPosition.TOP}
          alignment={TooltipAlignment.START}
        >
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}
          >
            <div>{params.data.package_name}</div>
          </div>
          <div
            slot="anchor"
            style={{
              marginLeft: '8px',
              overflow: 'hidden',
              minWidth: '0',
            }}
          >
            <div style={{
              color: 'var(--exo-color-font)',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '15px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            >
              {params.data.package_name}
            </div>
            <div style={{
              color: 'var(--exo-color-font-secondary)',
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: '15px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            >
              ID:
              {' '}
              {params.data.package_id}
            </div>
          </div>
        </ExTooltip>
      </div>,
    );

    return container;
  };

  const deployedOnCellRenderer = (params: any) => {
    const container = document.createElement('div');
    container.style.display = 'flex';
    container.style.alignItems = 'center';
    container.style.height = '100%';
    const root = createRoot(container);
    const deployedOn = params.data?.deployed_on || '-';
    const formattedDate = formatDate(deployedOn);
    root.render(
      <ExTooltip
        tooltipDelay={0}
        position={TooltipPosition.TOP}
        alignment={TooltipAlignment.MIDDLE}
      >
        <div
          style={{
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
            maxWidth: '250px',
            lineHeight: '1.4',
          }}
        >
          {formattedDate}
        </div>
        <div
          slot="anchor"
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: '14px',
            color: 'var(--exo-color-font)',
          }}
        >
          {formattedDate}
        </div>
      </ExTooltip>,
    );
    return container;
  };

  const columnDefs = [
    {
      field: 'package_name',
      headerName: t('ai_agent_garden_agent_name'),
      flex: 1,
      cellRenderer: (params: any) => agentNameCellRenderer(params),
      resizable: true,
    },
    {
      field: 'package_version',
      headerName: t('package_version', 'Package Version'),
      flex: 0.6,
      resizable: true,
    },
    {
      field: 'deployedOn',
      headerName: t('deployed_on', 'Deployed On'),
      flex: 1.2,
      cellRenderer: (params: any) => deployedOnCellRenderer(params),
      resizable: true,
    },
  ];

  const [gridOptions] = useState<any>({
    headerHeight: 36,
    defaultColDef: {
      resizable: true,
      sortable: false,
      cellStyle: { fontSize: '14px', fontFamily: 'Noto Sans' },
    },
    columnDefs,
    rowData: [],
    domLayout: 'normal',
    suppressPaginationPanel: true,
    rowHeight: 56,
    popupParent: document.querySelector('body'),
    suppressDragLeaveHidesColumns: true,
    suppressMovableColumns: true,
    suppressRowTransform: true,
    onGridReady: (event: any) => {
      event.api.sizeColumnsToFit();
      setGridApi(event.api);
    },
  });

  useEffect(() => {
    if (gridApi) {
      gridApi.setGridOption('rowData', agents);
    }
  }, [gridApi, agents]);

  return (
    <div className="model-agents-drawer">
      <ExSideDrawer
        panelTitle={`${t('deployed_agents', 'Deployed Agents')}: ${totalRowsRef.current}`}
        open={open}
        onClose={handleClose}
        onCancel={onClose}
        data-testid="model-agents-side-drawer"
        className={
          fromConfigurationsDrawer
            ? 'model-agents-drawer__content model-agents-drawer-from-configurations'
            : 'model-agents-drawer__content'
        }
        navigation={navigation}
        preventStacking={fromConfigurationsDrawer ? false : !navigation}
      >
        <div className="model-agents-drawer__body">
          <div className="model-agents-drawer__model-info">
            <div className="model-agents-drawer__model-icon-wrapper">
              <img
                src={providerLogo(provider).src}
                alt={providerLogo(provider).alt}
                className="model-agents-drawer__model-icon"
              />
            </div>
            <span className="model-agents-drawer__model-name">{modelName}</span>
          </div>

          <div className="model-agents-drawer__search-wrapper">
            <ExInput
              placeholder={t('search_by_agent_name', 'Search by agent name')}
              data-testid="model-agents-search"
              type="text"
              clearable
              size={SearchSize.MEDIUM}
              flavor={SearchFlavor.WHITE}
              onInput={(e: any) => {
                setSearchTerm(e.target.value);
                setSelectedPage(1);
              }}
            >
              <ExIcon icon="Search" variant={IconVariant.ICON} slot="prefix" />
            </ExInput>
          </div>

          {isLoading ? (
            <div className="model-agents-drawer__loader">
              <ExLoader
                size={LoaderSize.MEDIUM}
                label={t('ai_loading')}
                variant={LoaderVariant.SPINNER}
              />
            </div>
          ) : (
            <div className="model-agents-drawer__table-wrapper">
              <ExTable
                stickyHeader
                domLayoutNormal
                gridOptions={gridOptions}
                data-testid="model-agents-table"
                className="model-agents-table"
              />

              {totalRowsRef.current > 0 && (
                <div className="model-agents-drawer__pagination">
                  <ExPagination
                    totalItems={totalRowsRef.current}
                    pageSize={pageSize}
                    pageSizeOptions={[10]}
                    selectedPage={selectedPage}
                    onChange={(e: CustomEvent) => {
                      setSelectedPage(e.detail.selectedPage || 1);
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
        <div slot="footer" className="model-agents-drawer__footer">
          <ExButton
            type={ButtonType.SECONDARY}
            size={ButtonSize.DEFAULT}
            flavor={ButtonFlavor.BRANDED}
            onClick={handleClose}
            data-testid="model-agents-close-btn"
          >
            {t('close')}
          </ExButton>
        </div>
      </ExSideDrawer>
    </div>
  );
}

ModelAgentsDrawer.defaultProps = {
  provider: 'Boomi',
  navigation: false,
  fromConfigurationsDrawer: false,
  onParentClose: undefined,
};

export default ModelAgentsDrawer;
